import { Injectable } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, EventType } from '@azure/msal-browser';
import { Observable, from, throwError, Subject, BehaviorSubject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { MSALInstanceService } from './msal-instance.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly destroying$ = new Subject<void>();
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  private isAnonymousSubject = new BehaviorSubject<boolean>(
    localStorage.getItem('authType') === 'guest'
  );
  public isAnonymous$ = this.isAnonymousSubject.asObservable();
  
  private autoLoginAttemptedSubject = new BehaviorSubject<boolean>(false);
  public autoLoginAttempted$ = this.autoLoginAttemptedSubject.asObservable();
    
  private internalUserSubject = new BehaviorSubject<boolean>(false);
  public internalUser$ = this.internalUserSubject.asObservable();

  constructor(
    private msalService: MsalService, 
    private msalBroadcastService: MsalBroadcastService,
    private msalInstanceService: MSALInstanceService
  ) {
    // Check authentication state after construction to avoid accessing potentially null MSAL instance
    if (localStorage.getItem('authType') !== 'guest') {
      const accounts = this.msalService.instance?.getAllAccounts() || [];
      this.isAuthenticatedSubject.next(accounts.length > 0);
    } else {
      this.isAuthenticatedSubject.next(true);
    }
    
    this.initializeAuth();
    
    // Setup MSAL event handlers
    if (!this.isGuestMode()) {
      this.msalBroadcastService.msalSubject$.subscribe(event => {
        if (event.eventType === EventType.LOGIN_SUCCESS) {
          const authResult = event.payload as AuthenticationResult;
          this.msalService.instance?.setActiveAccount(authResult.account);
          this.isAuthenticatedSubject.next(true);
          this.autoLoginAttemptedSubject.next(true);
        }
        if (event.eventType === EventType.LOGOUT_SUCCESS) {
          this.isAuthenticatedSubject.next(false);
        }
      });
    }
  }

  updateLoginStatus(isLoggedIn: boolean): void {
    this.isAuthenticatedSubject.next(isLoggedIn);
    this.autoLoginAttemptedSubject.next(isLoggedIn);
  }

  setInternalUser(isInternal: boolean): void {
    this.internalUserSubject.next(isInternal);
  }

  loginAsGuest(): void {
    localStorage.setItem('authType', 'guest');
    this.isAnonymousSubject.next(true);
    this.isAuthenticatedSubject.next(true);
    this.autoLoginAttemptedSubject.next(true);
  }

  isGuestMode(): boolean {
    return localStorage.getItem('authType') === 'guest';
  }

  getAccessToken(): Observable<string> {
    if (this.isGuestMode()) {
      return from(Promise.resolve(''));
    }
    const accounts = this.msalService.instance?.getAllAccounts() || [];
    if (accounts.length === 0) {
      return from(Promise.resolve(''));
    }
    const request = {
      scopes: ['openid', 'profile', 'email'],
      account: accounts[0]
    };
    return from(this.msalService.acquireTokenSilent(request)).pipe(
      map(response => response?.idToken || ''),
      catchError(err => {
        console.error('Failed silent token acquisition:', err);
        return throwError(() => err);
      })
    );
  }

  refreshMediaServiceCookies(): Observable<boolean> {
    return this.getAccessToken().pipe(
      map(token => {
        if (token) {
          this.setJwtCookie(token);
          return true;
        }
        return false;
      }),
      catchError(() => from(Promise.resolve(false)))
    );
  }

  getToken(): string {
    if (this.isGuestMode()) {
      return '';
    }
    const accounts = this.msalService.instance?.getAllAccounts() || [];
    if (accounts.length !== 0 && accounts[0].idToken) {
      return accounts[0].idToken;
    }
    return '';
  }

  public setJwtCookie(token: string): void {
    const currentDomain = window.location.hostname;
    document.cookie = `jwt=${token}; path=/media; domain=${currentDomain}; SameSite=Strict`;
  }

  private async initializeAuth(): Promise<void> {
    try {
      if (this.isGuestMode()) {
        this.isAnonymousSubject.next(true);
        this.isAuthenticatedSubject.next(true);
        this.autoLoginAttemptedSubject.next(true);
        return;
      }

      await this.msalService.initialize().toPromise();
      const response = await this.msalService.instance?.handleRedirectPromise();
      
      if (response) {
        if (response && response.idToken) {
          this.setJwtCookie(response.idToken);
          this.isAuthenticatedSubject.next(true);
          // Use the current auth type from MSALInstanceService
          const authType = this.msalInstanceService.getCurrentAuthType();
          if (authType !== 'guest') {
            localStorage.setItem('authType', authType);
          }
        }
        this.autoLoginAttemptedSubject.next(true);
        return;
      }

      // Try automatic silent login
      await this.tryAutoLogin();
    } catch (error) {
      console.error('Error during auth initialization:', error);
      this.autoLoginAttemptedSubject.next(true);
    }
  }

  private async tryAutoLogin(): Promise<void> {
    try {
      const accounts = this.msalService.instance?.getAllAccounts() || [];
      if (accounts.length > 0) {
        const request = {
          scopes: ['openid', 'profile', 'email'],
          account: accounts[0]
        };

        const response = await this.msalService.acquireTokenSilent(request).toPromise();
        if (response && response.idToken) {
          this.setJwtCookie(response.idToken);
          // Use the current auth type from MSALInstanceService
          const authType = this.msalInstanceService.getCurrentAuthType();
          if (authType !== 'guest') {
            localStorage.setItem('authType', authType);
          }
        }
        this.isAuthenticatedSubject.next(true);
      }
    } catch (error) {
      this.isAuthenticatedSubject.next(false);
    } finally {
      this.autoLoginAttemptedSubject.next(true);
    }
  }

  login(): Observable<boolean> {
    return from(this.msalService.loginRedirect({
      scopes: ['user.read']
    })).pipe(
      map(() => {
        // Use the current auth type from MSALInstanceService
        const authType = this.msalInstanceService.getCurrentAuthType();
        if (authType !== 'guest') {
          localStorage.setItem('authType', authType);
        }
        return true;
      }),
      catchError(error => {
        console.error('Login failed', error);
        return throwError(() => new Error('Login failed'));
      })
    );
  }

  logout(): void {
    const authType = localStorage.getItem('authType');
    
    // Clear guest mode state
    if (authType === 'guest') {
      this.isAnonymousSubject.next(false);
      this.isAuthenticatedSubject.next(false);
    }
    
    this.clearJwtCookie();
    localStorage.removeItem('authType');
    
    // Only call MSAL logout if not in guest mode
    if (authType !== 'guest') {
      this.msalService.logoutRedirect();
    }
  }

  private clearJwtCookie(): void {
    document.cookie = `jwt=; path=/media; domain=${window.location.hostname}; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=Strict`;
    window.location.reload();
  }

  getUser(): AccountInfo | null {
    // If in guest mode, return a mock account for guest user
    if (this.isGuestMode()) {
      return {
        homeAccountId: 'guest',
        environment: 'guest',
        tenantId: 'guest',
        username: 'guest',
        localAccountId: 'guest',
        name: 'Guest User'
      } as AccountInfo;
    }
    
    const accounts = this.msalService.instance?.getAllAccounts() || [];
    return accounts.length > 0 ? accounts[0] : null;
  }

  ngOnDestroy(): void {
    this.destroying$.next(undefined);
    this.destroying$.complete();
  }
}